import type { RoomView } from '@chipless/shared';
import { Button, Card, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { buyInProblem, chipUnitOf } from '@chipless/shared';
import { useState } from 'react';
import { AmountInput, parseAmount, type AmountValue } from '../components/AmountInput.js';
import { pt, ptWithUnit, seatLabel } from '../format.js';
import { useStore } from '../store.js';
import { getSavedName, saveName } from '../token.js';

/** 参加画面（要件1.4〜1.6）。表示名、空席の選択、バイイン額の指定。 */
export function JoinScreen({ view }: { view: RoomView }): React.ReactElement {
  const send = useStore((s) => s.send);
  const pending = useStore((s) => s.pending);
  const [name, setName] = useState(getSavedName());
  const [buyInText, setBuyInText] = useState<AmountValue>(view.config.defaultBuyIn);
  const [seatNo, setSeatNo] = useState<number | null>(null);

  const trimmed = name.trim();
  const buyIn = parseAmount(buyInText);
  // サーバの着席の検証と同じ判定。入力欄は自由に編集でき、押せないのは着席ボタンだけ。
  const buyInIssue =
    buyIn === null ? 'バイイン額を入力してください' : buyInProblem(view.config, buyIn);
  const canSit = trimmed.length > 0 && buyInIssue === null && seatNo !== null;

  const sit = (): void => {
    if (seatNo === null || buyIn === null) return;
    saveName(trimmed);
    send({ type: 'seat:take', seatNo, buyIn, name: trimmed });
  };

  // 下端の余白は safe-bottom に任せる。p="md" はインラインで当たり、クラスの余白を上書きする。
  return (
    <Stack px="md" pt="md" gap="lg" className="safe-bottom">
      <Stack gap={4}>
        <Title order={3}>卓に参加する</Title>
        <Text size="sm" c="dimmed">
          ブラインド {ptWithUnit(view.config.smallBlind)} / {ptWithUnit(view.config.bigBlind)}
        </Text>
      </Stack>

      <TextInput
        label="表示名"
        placeholder="なまえ"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        maxLength={12}
        data-testid="join-name"
      />

      <Stack gap="xs">
        <Text size="sm" fw={600}>
          席を選ぶ
        </Text>
        <SimpleGrid cols={2} spacing="xs">
          {view.seats.map((seat) => (
            <Button
              key={seat.seatNo}
              variant={seatNo === seat.seatNo ? 'filled' : 'default'}
              disabled={seat.occupied}
              onClick={() => setSeatNo(seat.seatNo)}
              h="auto"
              py="sm"
              data-testid={`join-seat-${seat.seatNo}`}
            >
              <Stack gap={0} align="center">
                <Text size="sm">{seatLabel(seat.seatNo)}</Text>
                {/* 選べる席を目立たせる。埋まっている席は名前だけ読めればよい。 */}
                <Text size="xs" c={seat.occupied ? 'dimmed' : 'gray.2'}>
                  {seat.occupied ? seat.name : '空席'}
                </Text>
              </Stack>
            </Button>
          ))}
        </SimpleGrid>
      </Stack>

      <AmountInput
        label="バイイン額"
        description={`${pt(chipUnitOf(view.config))} 単位`}
        value={buyInText}
        onChange={setBuyInText}
        step={view.config.bigBlind}
        allowNegative={false}
        problem={buyInIssue}
        data-testid="join-buyin"
      />

      <Button size="xl" disabled={!canSit || pending} onClick={sit} data-testid="join-submit">
        着席する
      </Button>

      {view.hand !== null && (
        <Card withBorder padding="sm">
          <Text size="sm">ハンドが進行中です。着席すると次のハンドから参加します。</Text>
        </Card>
      )}
    </Stack>
  );
}
